import { darkClash } from './colors.js';
import { roomFor } from './fill.js';
import { capacityOf, EPSILON } from './propose.js';
const byText = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
const keyOf = (t) => `${t.drawer}:${t.x}:${t.y}`;
/**
 * Moves one assignment to another place. If the same article is already assigned there,
 * the pieces are added to that assignment (which keeps its id) and the moved one disappears.
 */
export function moveAssignment(assignments, id, to) {
    const moving = assignments.find((a) => a.id === id);
    if (!moving)
        return [...assignments];
    const twin = assignments.find((a) => a.id !== id &&
        a.article === moving.article &&
        a.target.kind === to.kind &&
        keyOf(a.target) === keyOf(to));
    if (twin) {
        return assignments
            .filter((a) => a.id !== id)
            .map((a) => (a.id === twin.id ? { ...a, pieces: a.pieces + moving.pieces } : a));
    }
    return assignments.map((a) => (a.id === id ? { ...a, target: to } : a));
}
/** Problems block; warnings need the operator's explicit second confirmation. */
export function checkPlacement(input) {
    const { facts, perBox, assignments } = input;
    const problems = [];
    const problem = (text) => {
        if (!problems.includes(text))
            problems.push(text);
    };
    // Totals per article: more than the need is a problem, fewer simply stay out.
    const needed = new Map();
    for (const n of input.needs)
        needed.set(n.article, (needed.get(n.article) ?? 0) + n.pieces);
    const given = new Map();
    for (const a of assignments) {
        if (!Number.isInteger(a.pieces) || a.pieces < 1) {
            problem(`I pezzi di ${a.article} non sono validi.`);
            continue;
        }
        given.set(a.article, (given.get(a.article) ?? 0) + a.pieces);
    }
    for (const [article, pieces] of [...given].sort(([x], [y]) => byText(x, y))) {
        const need = needed.get(article) ?? 0;
        if (pieces > need)
            problem(`Troppi pezzi di ${article}: ne arrivano ${need}.`);
    }
    // Group by destination, keeping the order of the assignments.
    const groups = new Map();
    for (const a of assignments) {
        const key = `${a.target.kind}@${keyOf(a.target)}`;
        const g = groups.get(key);
        if (g)
            g.list.push(a);
        else
            groups.set(key, { target: a.target, list: [a] });
    }
    const warnings = [];
    for (const { target, list } of groups.values()) {
        const where = target.drawer;
        let existing;
        if (target.kind === 'new') {
            const free = input.slots.some((s) => keyOf(s) === keyOf(target));
            if (!free) {
                problem(`Il posto in ${where} non è più libero.`);
                continue;
            }
        }
        else {
            existing = input.boxes.find((b) => keyOf(b) === keyOf(target));
            if (!existing) {
                problem(`La scatola in ${where} non c'è più.`);
                continue;
            }
            if (existing.container === 'other') {
                problem(`In ${where} non c'è una scatola di capi: qui non si mette merce.`);
                continue;
            }
            if (existing.contents.length === 0) {
                problem(`La scatola in ${where} è vuota: scegli un posto libero.`);
                continue;
            }
            if (existing.group === null) {
                const own = existing.contents.map((c) => c.article);
                const wrong = list.some((a) => !own.includes(a.article));
                if (wrong) {
                    problem(`La scatola in ${where} tiene un solo articolo: qui va solo ${own.join(', ')}.`);
                    continue;
                }
            }
        }
        // Everything that ends up in the destination: existing contents, then the assignments.
        const present = [...(existing?.contents ?? [])];
        let fill = 0;
        let known = true;
        const add = (article, pieces, isNew) => {
            const f = facts.get(article);
            if (!f) {
                problem(`Dell'articolo ${article} non si sa nulla: non si può controllare la scatola in ${where}.`);
                known = false;
                return;
            }
            const capacity = capacityOf(perBox, f.productId);
            if (capacity === null) {
                problem(`Non so quanti pezzi di ${article} stanno in una scatola.`);
                known = false;
                return;
            }
            if (isNew) {
                const room = roomFor(fill, capacity);
                if (pieces / capacity + fill > 1 + EPSILON) {
                    problem(`La scatola in ${where} è piena: ci stanno ancora ${room} pezzi di ${article}.`);
                }
            }
            fill += pieces / capacity;
        };
        for (const c of present)
            add(c.article, c.pieces, false);
        for (const a of list) {
            if (Number.isInteger(a.pieces) && a.pieces >= 1)
                add(a.article, a.pieces, true);
        }
        if (!known)
            continue;
        // Warnings: sizes and dark colours against everything else in the destination.
        const members = [
            ...present.map((c) => ({ article: c.article, own: false })),
            ...list.map((a) => ({ article: a.article, own: true })),
        ];
        for (const a of list) {
            const mine = facts.get(a.article);
            const others = members
                .filter((m) => m.article !== a.article)
                .map((m) => facts.get(m.article));
            const sizes = [...new Set(others.map((f) => f.size).filter((s) => s !== mine.size))].sort(byText);
            const idOf = (kind) => `${kind}:${a.id}:${target.drawer}:${target.x}:${target.y}`;
            if (sizes.length > 0) {
                warnings.push({
                    id: idOf('size'),
                    kind: 'size',
                    assignment: a.id,
                    text: `In questa scatola ci sono già ${sizes.join(', ')}, stai mettendo ${mine.size}.`,
                });
            }
            const clash = others
                .map((f) => f.color)
                .filter((c) => darkClash(c, mine.color))
                .sort(byText)[0];
            if (clash !== undefined) {
                warnings.push({
                    id: idOf('dark-colors'),
                    kind: 'dark-colors',
                    assignment: a.id,
                    text: `In questa scatola c'è già ${clash}: con ${mine.color} si confondono al prelievo.`,
                });
            }
        }
    }
    return problems.length > 0 ? { ok: false, problems } : { ok: true, warnings };
}
//# sourceMappingURL=check.js.map